import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import {
  FaMapMarkerAlt,
  FaRegHeart,
  FaStar,
  FaWifi,
  FaVideo,
  FaCoffee,
  FaPenSquare,
  FaUsers,
  FaCalendarAlt,
  FaClock,
  FaSearch,
  FaCheck,
  FaBolt,
  FaSnowflake,
  FaParking,
} from 'react-icons/fa';
import { MdCastForEducation } from 'react-icons/md';
import Navbar from '../../components/Navbar';
import MeetingSearchResultsMap from '../../components/MeetingSearchResultsMap';
import { roomsAPI } from '../../services/meetingsAPI';
import { getImageUrl } from '../../utils/imageHelper';
import 'react-datepicker/dist/react-datepicker.css';
import './MeetingSearchResults.css';

const ROOM_TYPE_FILTERS = [
  { id: 'Boardroom', label: 'Board Room' },
  { id: 'Conference', label: 'Conference Room' },
  { id: 'Training', label: 'Training Room' },
  { id: 'Executive', label: 'Executive Suite' },
];

const AMENITY_FILTER_OPTIONS = [
  { id: 'Wi-Fi', Icon: FaWifi },
  { id: 'Projector', Icon: MdCastForEducation },
  { id: 'Whiteboard', Icon: FaPenSquare },
  { id: 'Air Conditioning', Icon: FaSnowflake },
  { id: 'Parking', Icon: FaParking },
  { id: 'Video Conferencing', Icon: FaVideo },
  { id: 'Coffee/Tea', Icon: FaCoffee },
];

const QUICK_AMENITY_CHIPS = ['Wi-Fi', 'Projector', 'Whiteboard', 'AC', 'Parking'];

const PRICE_SLIDER_MIN = 0;
const PRICE_SLIDER_MAX = 5000;
const ROOM_CARD_IMG_FALLBACK =
  'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800';

const normalizeAmenity = (s) => String(s || '').toLowerCase().replace(/[-\s/]/g, '');

const roomMatchesTypes = (room, selected) => {
  if (!selected.length) return true;
  const hay = `${room.roomType || ''} ${room.name || ''}`.toLowerCase();
  return selected.some((t) => {
    if (t === 'Executive' || t === 'Executive Suite') {
      return /executive|suite|deluxe|luxury/.test(hay);
    }
    if (t === 'Training' || t === 'Training Room') {
      return /training|workshop|seminar/.test(hay);
    }
    if (t === 'Boardroom' || t === 'Board Room') {
      return /board|boardroom/.test(hay);
    }
    if (t === 'Conference' || t === 'Conference Room') {
      return /conference|meeting/.test(hay);
    }
    return hay.includes(String(t).toLowerCase());
  });
};

const roomHasAmenities = (room, required) => {
  if (!required.length) return true;
  const roomNorm = (room.amenities || []).map(normalizeAmenity);
  return required.every((req) => {
    const r = normalizeAmenity(req);
    if (r === 'ac' || r === 'airconditioning') {
      return roomNorm.some((a) => a.includes('ac') || a.includes('air'));
    }
    return roomNorm.some((a) => a.includes(r) || r.includes(a) || a === r);
  });
};

const getOwnerId = (room) => {
  if (!room) return '';
  if (room.ownerId) return String(room.ownerId);
  const uid = room.userId;
  if (!uid) return '';
  if (typeof uid === 'string') return uid;
  return String(uid._id || uid.id || '');
};

const isRoomOwnedByUser = (room, user) => {
  if (!room || !user) return false;
  const currentUserId = String(user._id || user.id || '');
  const ownerId = getOwnerId(room);
  if (currentUserId && ownerId && currentUserId === ownerId) return true;
  const currentEmail = String(user.email || '').trim().toLowerCase();
  const ownerEmail = String(room.hostEmail || room.ownerEmail || '').trim().toLowerCase();
  return Boolean(currentEmail && ownerEmail && currentEmail === ownerEmail);
};

const getEntityId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value._id || value.id || value);
};

const isSameCompanyRoomForUser = (room, user) => {
  const roomCompanyId = getEntityId(room?.companyId);
  const userCompanyId = getEntityId(user?.company);
  return Boolean(roomCompanyId && userCompanyId && roomCompanyId === userCompanyId);
};

const hasCompanyWorkspace = (user) =>
  Boolean(getEntityId(user?.company || user?.companyId));

const isPublicRoom = (room) => String(room?.visibility || '').toLowerCase() === 'public';

const isOwnerBookingBlocked = (room, user) => {
  if (!room || !user) return false;
  if (isPublicRoom(room)) return false;
  if (!hasCompanyWorkspace(user)) return false;
  return isRoomOwnedByUser(room, user) && !isSameCompanyRoomForUser(room, user);
};

const parseLocationLabel = (room) => {
  const loc = room?.location;
  if (typeof loc === 'string' && loc.trim()) return loc.trim();
  if (loc && typeof loc === 'object') {
    const city = loc.city || loc.town || '';
    const area = loc.area || loc.neighborhood || '';
    return [area, city].filter(Boolean).join(', ') || loc.address || room?.address || '—';
  }
  const parts = [room?.address, room?.city, room?.state].filter((p) => String(p || '').trim());
  return parts.length ? parts.join(', ') : '—';
};

const parseLocationCoords = (room) => {
  const loc = room?.location;
  if (loc && typeof loc === 'object') {
    const lat = loc.lat ?? loc.latitude;
    const lng = loc.lng ?? loc.lon ?? loc.longitude;
    if (lat != null && lng != null) {
      const nlat = Number(lat);
      const nlng = Number(lng);
      if (Number.isFinite(nlat) && Number.isFinite(nlng) && (nlat !== 0 || nlng !== 0)) {
        return { lat: nlat, lng: nlng };
      }
    }
  }
  return null;
};

const MeetingSearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [filters, setFilters] = useState({
    roomType: [],
    amenities: [],
    priceRange: [0, PRICE_SLIDER_MAX],
    capacityMin: 0,
    capacityMax: 9999,
    instantBookOnly: false,
    availableOnly: false,
  });

  const [sortBy, setSortBy] = useState('popular');
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState(null);

  const [formLocation, setFormLocation] = useState(searchParams.get('location') || '');
  const [formDate, setFormDate] = useState(() => {
    const d = searchParams.get('date');
    return d ? new Date(`${d}T12:00:00`) : null;
  });
  const [formStartTime, setFormStartTime] = useState(searchParams.get('startTime') || '10:00');
  const [formEndTime, setFormEndTime] = useState(searchParams.get('endTime') || '13:00');
  const [formParticipants, setFormParticipants] = useState(
    parseInt(searchParams.get('participants'), 10) || 10
  );

  const searchCriteria = useMemo(
    () => ({
      location: searchParams.get('location'),
      date: searchParams.get('date'),
      startTime: searchParams.get('startTime'),
      endTime: searchParams.get('endTime'),
      participants: parseInt(searchParams.get('participants'), 10) || 0,
    }),
    [searchParams]
  );

  const resolveRoomImage = (room) => {
    const firstImage = Array.isArray(room.images) && room.images.length > 0 ? room.images[0] : null;
    const candidate = firstImage || room.image || '';
    if (candidate.startsWith('http') || candidate.startsWith('data:')) return candidate;
    if (candidate) return getImageUrl(candidate);
    return ROOM_CARD_IMG_FALLBACK;
  };

  useEffect(() => {
    const fetchRooms = async () => {
      setLoading(true);
      try {
        const apiParams = {};
        if (searchCriteria.location) apiParams.location = searchCriteria.location;
        if (searchCriteria.participants > 0) apiParams.capacity = searchCriteria.participants;
        const res = await roomsAPI.getRooms(apiParams);
        const data = Array.isArray(res?.data) ? res.data : [];
        const backendRooms = data
          .filter((r) => r.isActive !== false)
          .filter((r) => String(r?.visibility || 'public').toLowerCase() === 'public')
          .map((room) => {
            const coords = parseLocationCoords(room);
            const locationLabel = parseLocationLabel(room);
            return {
              id: room._id || room.id,
              _id: room._id || room.id,
              name: room.name || room.roomName,
              userId: room.userId || null,
              ownerId:
                room.ownerId ||
                (typeof room.userId === 'object' ? room.userId?._id || room.userId?.id : room.userId) ||
                '',
              hostEmail: room.hostEmail || room.ownerEmail || room.userId?.email || '',
              location: locationLabel,
              locationLabel,
              locationCoords: coords,
              address: room.address || '',
              building: typeof room.location === 'string' ? room.location : '',
              city: room.city || '',
              state: room.state || '',
              pincode: room.pincode || '',
              capacity: room.capacity || 0,
              roomType: room.roomType || 'Meeting Room',
              pricePerHour: room.pricePerHour || 0,
              rating: room.rating ?? 4.5,
              reviews: room.reviews ?? 0,
              verified: !!room.verified,
              amenities: room.amenities || [],
              instantBook: room.source === 'local' || room.source === 'internal' || !room.source,
              image: resolveRoomImage(room),
              source: room.source || 'local',
              externalLink: room.externalLink || room.website || null,
              companyId: room.companyId,
            };
          });
        setRooms(backendRooms);
      } catch {
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, [searchParams]);

  const getFilteredRooms = useCallback(() => {
    let filtered = [...rooms];
    if (filters.roomType.length > 0) {
      filtered = filtered.filter((room) => roomMatchesTypes(room, filters.roomType));
    }
    if (filters.amenities.length > 0) {
      filtered = filtered.filter((room) => roomHasAmenities(room, filters.amenities));
    }
    filtered = filtered.filter(
      (room) =>
        room.pricePerHour >= filters.priceRange[0] && room.pricePerHour <= filters.priceRange[1]
    );
    filtered = filtered.filter((room) => {
      const cap = room.capacity || 0;
      if (filters.capacityMin > 0 && cap < filters.capacityMin) return false;
      if (filters.capacityMax < 9999 && cap > filters.capacityMax) return false;
      return true;
    });
    if (filters.instantBookOnly) {
      filtered = filtered.filter((r) => r.instantBook);
    }
    if (filters.availableOnly) {
      filtered = filtered.filter((r) => r.instantBook || r.source === 'local');
    }
    if (searchCriteria.location) {
      const locClean = searchCriteria.location
        .replace(/meeting\s*rooms?\s*(in|at|near)?/gi, '')
        .trim();
      const searchTerm = (locClean || searchCriteria.location).toLowerCase();
      if (searchTerm) {
        filtered = filtered.filter((room) => {
          if (room.source === 'serpapi') return true;
          const loc = (room.location || '').toLowerCase();
          const name = (room.name || '').toLowerCase();
          return loc.includes(searchTerm) || name.includes(searchTerm);
        });
      }
    }
    if (searchCriteria.participants > 0) {
      filtered = filtered.filter((room) => (room.capacity || 0) >= searchCriteria.participants);
    }
    return filtered;
  }, [rooms, filters, searchCriteria]);

  const getSortedRooms = (list) => {
    const sorted = [...list];
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => a.pricePerHour - b.pricePerHour);
      case 'price-high':
        return sorted.sort((a, b) => b.pricePerHour - a.pricePerHour);
      case 'rating':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'capacity-large':
        return sorted.sort((a, b) => b.capacity - a.capacity);
      default:
        return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
  };

  const filteredAndSortedRooms = useMemo(
    () => getSortedRooms(getFilteredRooms()),
    [getFilteredRooms, sortBy]
  );

  const roomTypeCounts = useMemo(() => {
    const counts = {};
    ROOM_TYPE_FILTERS.forEach((t) => {
      counts[t.id] = rooms.filter((r) => roomMatchesTypes(r, [t.id])).length;
    });
    return counts;
  }, [rooms]);

  const toggleFilter = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter((item) => item !== value)
        : [...prev[filterType], value],
    }));
  };

  const toggleQuickAmenity = (chip) => {
    const map = { AC: 'Air Conditioning' };
    const id = map[chip] || chip;
    toggleFilter('amenities', id);
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (formLocation.trim()) params.set('location', formLocation.trim());
    if (formDate) params.set('date', format(formDate, 'yyyy-MM-dd'));
    if (formStartTime) params.set('startTime', formStartTime);
    if (formEndTime) params.set('endTime', formEndTime);
    if (formParticipants) params.set('participants', String(formParticipants));
    setSearchParams(params);
  };

  const handleReset = () => {
    setFormLocation('');
    setFormDate(null);
    setFormStartTime('10:00');
    setFormEndTime('13:00');
    setFormParticipants(10);
    setSearchParams({});
    setFilters({
      roomType: [],
      amenities: [],
      priceRange: [0, PRICE_SLIDER_MAX],
      capacityMin: 0,
      capacityMax: 9999,
      instantBookOnly: false,
      availableOnly: false,
    });
  };

  return (
    <div className="meeting-search-results-page meeting-search-results-page--premium page-content">
      <Navbar isScrolled={false} />

      <div className="msr-premium-toolbar">
        <div className="msr-premium-toolbar-inner">
          <div className="msr-search-field">
            <FaMapMarkerAlt className="msr-field-icon" aria-hidden />
            <input
              type="text"
              placeholder="Enter city or area"
              value={formLocation}
              onChange={(e) => setFormLocation(e.target.value)}
            />
          </div>
          <div className="msr-search-field">
            <FaCalendarAlt className="msr-field-icon" aria-hidden />
            <DatePicker
              selected={formDate}
              onChange={(d) => setFormDate(d)}
              placeholderText="Select date"
              dateFormat="dd MMM yyyy"
              minDate={new Date()}
              className="msr-date-input"
            />
          </div>
          <div className="msr-search-field msr-search-field--time">
            <FaClock className="msr-field-icon" aria-hidden />
            <input
              type="time"
              value={formStartTime}
              onChange={(e) => setFormStartTime(e.target.value)}
              aria-label="Start time"
            />
            <span className="msr-time-sep">–</span>
            <input
              type="time"
              value={formEndTime}
              onChange={(e) => setFormEndTime(e.target.value)}
              aria-label="End time"
            />
          </div>
          <div className="msr-search-field msr-search-field--narrow">
            <FaUsers className="msr-field-icon" aria-hidden />
            <input
              type="number"
              min={1}
              max={200}
              value={formParticipants}
              onChange={(e) => setFormParticipants(Math.max(1, Number(e.target.value) || 1))}
              aria-label="Number of people"
            />
            <span className="msr-people-suffix">People</span>
          </div>
          <button type="button" className="msr-search-btn" onClick={handleSearch}>
            <FaSearch aria-hidden />
            Search
          </button>
          <button type="button" className="msr-reset-link" onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>

      <div className="msr-premium-body">
        <aside className={`msr-filters ${showFilters ? 'open' : ''}`}>
          <div className="msr-filters-head">
            <h2>Filters</h2>
            <button type="button" className="msr-clear-link" onClick={handleReset}>
              Clear All
            </button>
          </div>

          <div className="msr-filter-block">
            <h3>Price Range (per hour)</h3>
            <div className="msr-price-labels">
              <span>₹{filters.priceRange[0].toLocaleString('en-IN')}</span>
              <span>₹{filters.priceRange[1].toLocaleString('en-IN')}+</span>
            </div>
            <input
              type="range"
              min={PRICE_SLIDER_MIN}
              max={PRICE_SLIDER_MAX}
              step={100}
              value={filters.priceRange[1]}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  priceRange: [prev.priceRange[0], Number(e.target.value)],
                }))
              }
              className="msr-range"
            />
          </div>

          <div className="msr-filter-block">
            <h3>Room Capacity</h3>
            <select
              className="msr-select"
              value={
                filters.capacityMin <= 0
                  ? 'any'
                  : filters.capacityMin >= 13
                    ? '13'
                    : filters.capacityMin >= 5
                      ? '5'
                      : '1'
              }
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  capacityMin: v === 'any' ? 0 : v === '1' ? 1 : v === '5' ? 5 : 13,
                }));
              }}
            >
              <option value="any">Any</option>
              <option value="1">1–4 People</option>
              <option value="5">5–12 People</option>
              <option value="13">13+ People</option>
            </select>
          </div>

          <div className="msr-filter-block">
            <h3>Room Type</h3>
            <ul className="msr-check-list">
              {ROOM_TYPE_FILTERS.map((type) => (
                <li key={type.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={filters.roomType.includes(type.id)}
                      onChange={() => toggleFilter('roomType', type.id)}
                    />
                    <span>{type.label}</span>
                    <span className="msr-count">{roomTypeCounts[type.id] ?? 0}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div className="msr-filter-block">
            <h3>Amenities</h3>
            <ul className="msr-check-list">
              {AMENITY_FILTER_OPTIONS.map(({ id, Icon }) => (
                <li key={id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={filters.amenities.includes(id)}
                      onChange={() => toggleFilter('amenities', id)}
                    />
                    <Icon className="msr-amenity-icon" aria-hidden />
                    <span>{id}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div className="msr-filter-block msr-filter-block--toggles">
            <label>
              <input
                type="checkbox"
                checked={filters.instantBookOnly}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, instantBookOnly: e.target.checked }))
                }
              />
              <span>Instant Book</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={filters.availableOnly}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, availableOnly: e.target.checked }))
                }
              />
              <span>Only show available rooms</span>
            </label>
          </div>

          <button type="button" className="msr-apply-btn" onClick={() => setShowFilters(false)}>
            Apply Filters
          </button>
        </aside>

        <div
          className={`msr-filters-overlay ${showFilters ? 'open' : ''}`}
          onClick={() => setShowFilters(false)}
          aria-hidden={!showFilters}
        />

        <section className="msr-results">
          <div className="msr-results-head">
            <div>
              <h1>All Meeting Rooms</h1>
              <p className="msr-results-meta">
                {loading ? 'Searching…' : `${filteredAndSortedRooms.length} results found`}
                {searchCriteria.location ? ` in ${searchCriteria.location}` : ''}
              </p>
            </div>
            <button
              type="button"
              className="msr-mobile-filter-btn"
              onClick={() => setShowFilters(true)}
            >
              Filters
            </button>
          </div>

          <div className="msr-quick-chips">
            {QUICK_AMENITY_CHIPS.map((chip) => {
              const mapped = chip === 'AC' ? 'Air Conditioning' : chip;
              const active = filters.amenities.includes(mapped);
              return (
                <button
                  key={chip}
                  type="button"
                  className={`msr-chip ${active ? 'active' : ''}`}
                  onClick={() => toggleQuickAmenity(chip)}
                >
                  {chip}
                </button>
              );
            })}
          </div>

          <div className="msr-toolbar-row">
            <label className="msr-sort-label">
              Sort by:
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="popular">Popular</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Rating</option>
                <option value="capacity-large">Capacity</option>
              </select>
            </label>
          </div>

          {loading && (
            <div className="msr-loading">
              <div className="msr-spinner" />
              <p>Searching for available rooms…</p>
            </div>
          )}

          {!loading && filteredAndSortedRooms.length === 0 && (
            <div className="msr-empty">
              <h3>No rooms available</h3>
              <p>Try adjusting filters or search in another area.</p>
            </div>
          )}

          {!loading && (
            <div className="msr-cards-list">
              {filteredAndSortedRooms.map((room) => {
                const hid = room.id || room._id;
                const isOwn = isOwnerBookingBlocked(room, currentUser);
                return (
                  <article
                    key={hid}
                    id={`msr-room-${hid}`}
                    className={`msr-room-card ${String(selectedRoomId) === String(hid) ? 'active' : ''}`}
                    onMouseEnter={() => setSelectedRoomId(hid)}
                  >
                    <div className="msr-room-card__media">
                      <img
                        src={room.image}
                        alt={room.name}
                        loading="lazy"
                        onError={(e) => {
                          if (e.currentTarget.dataset.fb) return;
                          e.currentTarget.dataset.fb = '1';
                          e.currentTarget.src = ROOM_CARD_IMG_FALLBACK;
                        }}
                      />
                      {room.instantBook && (
                        <span className="msr-instant-badge">
                          <FaBolt aria-hidden />
                          Instant Book
                        </span>
                      )}
                      <button type="button" className="msr-fav-btn" aria-label="Save room">
                        <FaRegHeart />
                      </button>
                    </div>

                    <div className="msr-room-card__body">
                      <div className="msr-room-card__top">
                        <div>
                          <h3 className="msr-room-title">
                            {room.name}
                            {room.verified && (
                              <span className="msr-verified" title="Verified">
                                <FaCheck aria-hidden />
                              </span>
                            )}
                          </h3>
                          <p className="msr-room-loc">
                            <FaMapMarkerAlt aria-hidden />
                            {room.location}
                            {room.city ? ` • ${room.city}` : ''}
                          </p>
                        </div>
                        <div className="msr-room-rating">
                          <FaStar aria-hidden />
                          <span>{Number(room.rating).toFixed(1)}</span>
                          <small>({room.reviews || 0} reviews)</small>
                        </div>
                      </div>

                      <div className="msr-room-meta">
                        <span>{room.capacity} Seats</span>
                        <span className="msr-dot">•</span>
                        <span>{room.roomType}</span>
                      </div>

                      <div className="msr-room-amenity-icons">
                        <FaWifi title="Wi-Fi" />
                        <MdCastForEducation title="Projector" />
                        <FaSnowflake title="AC" />
                      </div>

                      <span className="msr-available-pill">Available</span>
                    </div>

                    <div className="msr-room-card__price">
                      <p className="msr-price-main">
                        ₹{Number(room.pricePerHour).toLocaleString('en-IN')}
                        <span>/hour</span>
                      </p>
                      <p className="msr-price-tax">incl. taxes</p>
                      {isOwn ? (
                        <button type="button" className="msr-view-btn" disabled>
                          Your listing
                        </button>
                      ) : ['serpapi', 'geoapify'].includes(room.source) && room.externalLink ? (
                        <a
                          href={room.externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="msr-view-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Details ↗
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="msr-view-btn"
                          onClick={() =>
                            navigate(
                              `/meetings/book-flow?roomId=${encodeURIComponent(hid)}`
                            )
                          }
                        >
                          View Details
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <MeetingSearchResultsMap
          rooms={filteredAndSortedRooms}
          searchLocation={searchCriteria.location || formLocation}
          selectedRoomId={selectedRoomId}
          onSelectRoom={(room) => {
            const hid = room._id || room.id;
            setSelectedRoomId(hid);
            document.getElementById(`msr-room-${hid}`)?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }}
        />
      </div>
    </div>
  );
};

export default MeetingSearchResults;
